import React from 'react';
import styled from 'styled-components';
import { MobileCardContainer } from '../styles/DashboardMobileStyles';
import { colors, typography, spacing } from '../styles/designTokens';
import type { KeywordStatusVariant } from './KeywordStatusSplitCard';

export interface KeywordStatusSplitCardMobileProps {
  variant: KeywordStatusVariant;
  autoCount: number;
  manualCount: number;
  formatNumber: (n: number) => string;
  clickable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}

const MobileCardTitle = styled.h3`
  margin: 0 0 ${spacing[2]} 0;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.semantic.text.tertiary};
  line-height: ${typography.lineHeight.tight};
`;

const MobileMainValue = styled.div`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.semantic.text.primary};
  margin-bottom: ${spacing[1]};
  line-height: ${typography.lineHeight.tight};
`;

const MobileSectionLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: ${colors.semantic.text.secondary};
  margin-bottom: ${spacing[1]};
`;

const MobileChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
`;

const MobileMetaChip = styled.div<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${colors.semantic.border.primary};
  background: ${colors.secondary[50]};
  color: ${({ $color }) => $color || colors.secondary[600]};
  font-size: 11px;
  font-weight: 800;
  white-space: nowrap;
`;

const ChipLabel = styled.span`
  font-weight: 900;
`;

const ChipValue = styled.span`
  font-weight: 900;
`;

const titles: Record<KeywordStatusVariant, string> = {
  active: '키워드 현황 (활성)',
  inactive: '키워드 현황 (비활성)'
};

export default function KeywordStatusSplitCardMobile({
  variant,
  autoCount,
  manualCount,
  formatNumber,
  clickable = false,
  isActive = false,
  onClick
}: KeywordStatusSplitCardMobileProps) {
  const total = autoCount + manualCount;
  const isActiveTone = variant === 'active';

  return (
    <MobileCardContainer
      $clickable={clickable}
      $isActive={isActive}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable && onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? isActive : undefined}
    >
      <MobileCardTitle>{titles[variant]}</MobileCardTitle>
      <MobileMainValue>
        {formatNumber(total)}
        <span
          style={{
            fontSize: '14px',
            marginLeft: '4px',
            fontWeight: 900,
            color: colors.semantic.text.tertiary
          }}
        >
          건
        </span>
      </MobileMainValue>

      <MobileSectionLabel>자동·수동</MobileSectionLabel>
      <MobileChipRow style={{ marginBottom: 0 }}>
        <MobileMetaChip
          $color={isActiveTone ? colors.success[600] : colors.semantic.text.tertiary}
          title={`자동: ${autoCount}`}
        >
          <ChipLabel>자동</ChipLabel>
          <ChipValue>{formatNumber(autoCount)}</ChipValue>
        </MobileMetaChip>
        <MobileMetaChip
          $color={isActiveTone ? colors.error[600] : colors.semantic.text.tertiary}
          title={`수동: ${manualCount}`}
        >
          <ChipLabel>수동</ChipLabel>
          <ChipValue>{formatNumber(manualCount)}</ChipValue>
        </MobileMetaChip>
      </MobileChipRow>
    </MobileCardContainer>
  );
}
